import {
  Map as ImmutableMap,
  Set as ImmutableSet,
  isImmutable,
  List,
  type OrderedMap,
  Record,
  type RecordOf,
} from 'immutable';
import { DateTime } from 'luxon';
import { createCachedSelector, LruObjectCache } from 're-reselect';
import { createSelector } from 'reselect';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import * as investmentsQuotesDetailedSelectors from 'app-components/flux/investments-quotes-detailed/investmentsQuotesDetailedSelectors';
import type { DetailedQuote } from 'app-components/flux/investments-quotes-detailed/investmentsQuotesDetailedTypes';
import {
  keyFromHolding,
  keyFromSecurity,
} from 'app-components/flux/investments-quotes-detailed/investmentsQuotesDetailedUtils';
import * as securitiesSelectors from 'app-components/flux/securities/securitiesSelectors';
import * as securitiesUtils from 'app-components/flux/securities/securitiesUtils';
import { createAppSelector } from 'app-components/utils/core/reselect';
import { registerCachedSelectors } from 'app-components/utils/core/reselect/selectorCacheRegistry';
import type RootState from 'app-components/utils/redux-store/rootState';

import { accountsSelectors } from '../accounts';
import type { Account } from '../accounts/accountsTypes';
import { selectorsHelpers } from '../core';
import { investmentQuotesV2Selectors, investmentQuotesV2Utils } from '../investment-quotes-v2';
import { SecurityTypeEnum } from '../securities/securitiesTypes';
import {
  columns,
  type costBasisStatus,
  type HoldingNodeType,
  INVESTMENT_HOLDINGS_V2_REDUCER_KEY,
  type INVESTMENT_HOLDINGS_V2_REDUCER_KEY_TYPE,
  type InvestmentHoldingV2Type,
} from './investmentHoldingsV2Types';

type State = Pick<RootState, INVESTMENT_HOLDINGS_V2_REDUCER_KEY_TYPE>;

const priceScaleForSecurityType = (securityType?: string | null) => (securityType === SecurityTypeEnum.BOND ? 100 : 1);

const valueFromPrice = (price: number, quantity: number, securityType?: string | null) =>
  (price * quantity) / priceScaleForSecurityType(securityType);

const getSecurityTypesById = selectorsHelpers.createTrimSelector(
  (state: Parameters<typeof securitiesSelectors.getResourcesById>[0]) =>
    securitiesSelectors.getResourcesById(state) ?? ImmutableMap(),
  (security) => security?.securityType,
);

export const getResourceStore = (state: State) => state[INVESTMENT_HOLDINGS_V2_REDUCER_KEY];

export const getLoadPending = (state: State) => getResourceStore(state).loadPending;
export const getIsLoading = (state: State) => getResourceStore(state).isLoading;
export const getError = (state: State) => getResourceStore(state).error;
export const getResourcesById = (state: State) => getResourceStore(state).resourcesById;
export const getLastSyncDate = (state: State) => getResourceStore(state).lastSyncDate;

export const getManualHoldingsWithLocalSecurityId = createAppSelector(
  getResourcesById,
  accountsSelectors.getNonConnectedInvestmentAccounts,
  (_state: State, localSecurityId?: string) => localSecurityId,
  (holdings, accounts, localSecurityId) => {
    const holdingsInAccounts = holdings.filter((holding) => accounts.has(holding.accountId));
    return ImmutableMap(
      holdingsInAccounts.filter((holding) => holding.localSecurityId === localSecurityId),
    ) as ImmutableMap<string, RecordOf<InvestmentHoldingV2Type>>;
  },
);

const getInvestmentHoldingsForAccount = createAppSelector(
  getResourcesById,
  (_state: State, accountId: string) => accountId,
  (holdings, accountId) => {
    if (!accountId) {
      return holdings;
    }
    return holdings.filter((holding) => holding.accountId === accountId);
  },
);

export const getInvestmentHoldingsByAccountId = createAppSelector(
  getResourcesById,
  (holdings) =>
    holdings.toList().groupBy((holding) => holding.accountId) as unknown as ImmutableMap<
      string,
      List<RecordOf<InvestmentHoldingV2Type>>
    >,
);

const getInvestmentHoldingsIdsByAccountId = selectorsHelpers.createTrimSelector(
  getInvestmentHoldingsByAccountId as any,
  (holdingList: any) => holdingList.map(({ id }: { id: string }) => id),
);

export const getCashByAccountId = createAppSelector(
  getInvestmentHoldingsByAccountId,
  (_state: State, props: { accountId?: string }) => props.accountId,
  (holdings, accountId) =>
    accountId ? holdings.get(accountId)?.find((holding: RecordOf<InvestmentHoldingV2Type>) => !!holding.isCash) : null,
);

/*
 *  This is done because the marketValue and price in manual holdings is NOT updated every refresh to reflect a
 *  non-manual stocks' new price. So we take the quantity and multiply it by the new price, pass it to this function,
 *  and now we have updated values!
 */
const rebuildHoldingWithRealValues = (
  name: string | undefined,
  marketValue: number,
  price: number,
  holdingItem: RecordOf<InvestmentHoldingV2Type>,
  accountType: string | null | undefined,
) => {
  if (!holdingItem) {
    return undefined;
  }
  return {
    name,
    accountId: holdingItem.accountId,
    subType: accountType,
    clientId: holdingItem.clientId,
    createdAt: holdingItem.createdAt,
    currentPrice: price,
    id: holdingItem.id,
    isCash: holdingItem.isCash,
    isDeleted: holdingItem.isDeleted,
    marketValue,
    quantity: holdingItem.quantity,
    securityName: holdingItem.securityName,
    symbol: holdingItem?.symbol?.trim(),
    localSecurityId: holdingItem?.localSecurityId,
    costBasis: holdingItem?.costBasis,
    userCostBasis: holdingItem?.userCostBasis,
    userCostBasisOn: holdingItem?.userCostBasisOn,
    quantityWhenUserSetCostBasis: holdingItem?.quantityWhenUserSetCostBasis,
  };
};

type RebuildHoldingResult = ReturnType<typeof rebuildHoldingWithRealValues>;

/*
 *  Get the sorted holdings of a specific stock
 *  stockData: All of the information on the selected stock
 */
export const getSortedAccountsMap = createAppSelector(
  accountsSelectors.getInvestmentAccounts,
  (_state: State, props: { node: HoldingNodeType }) => props.node,
  (state: Parameters<typeof investmentQuotesV2Selectors.getQuoteListByLocalSecurityId>[0]) =>
    investmentQuotesV2Selectors.getQuoteListByLocalSecurityId(state),
  (state: Parameters<typeof featureFlagsSelectors.getFeatureFlag>[0]) =>
    featureFlagsSelectors.getFeatureFlag(state, 'relyOnSecurities'),
  (investmentAccounts, stockData, quotesBySecurityId, securitiesFF) => {
    if (investmentAccounts && stockData) {
      const filteredAccounts = investmentAccounts.filter((account: Account) => {
        let isContained = false;
        stockData.holdings.forEach((myHolding: InvestmentHoldingV2Type) => {
          if (myHolding.accountId === account.id) {
            isContained = true;
          }
        });
        return isContained;
      });
      const newList = filteredAccounts.map((acct: Account) => {
        const { name } = acct;
        let price: number | undefined = 0;
        let marketValue: number | null = 0;
        let holdingItem: RecordOf<InvestmentHoldingV2Type> | undefined;
        stockData.holdings.forEach((myHolding: InvestmentHoldingV2Type) => {
          if (myHolding.accountId === acct.id) {
            if (securitiesFF) {
              const quote = myHolding.localSecurityId ? quotesBySecurityId.get(myHolding.localSecurityId) : undefined;
              const isBarchartQuote = investmentQuotesV2Utils.isInvestmentQuote(quote) && !!quote.lastPrice;
              price = securitiesUtils.getPrice(quote);
              marketValue =
                myHolding.isCash || !isBarchartQuote
                  ? myHolding.marketValue ||
                    (typeof price === 'number' ? price : stockData.price) * (myHolding.quantity ?? 0)
                  : (typeof price === 'number' ? price : stockData.price) * (myHolding.quantity ?? 0);
            } else {
              const quote = investmentQuotesV2Utils.getQuoteBySymbol(quotesBySecurityId, myHolding?.symbol?.trim());
              const isQuote = investmentQuotesV2Utils.isInvestmentQuote(quote);
              price = (isQuote && quote.currentPrice) || stockData?.price || myHolding?.currentPrice || 0;
              const isBarchartQuote = isQuote && !!quote.lastPrice;
              marketValue =
                typeof price === 'number' && !myHolding.isCash && quote
                  ? isBarchartQuote
                    ? price * (myHolding.quantity ?? 0)
                    : myHolding.marketValue
                  : myHolding.marketValue;
            }
            holdingItem = myHolding as unknown as RecordOf<InvestmentHoldingV2Type>;
          }
        });
        return rebuildHoldingWithRealValues(name, marketValue, price, holdingItem!, acct.subType);
      });
      return newList.sort((a: RebuildHoldingResult, b: RebuildHoldingResult) => a!.marketValue - b!.marketValue);
    }
    return null;
  },
) as unknown as (state: any, props: any) => OrderedMap<string, InvestmentHoldingV2Type & { name: string }> | null;

const mkHoldingNode = Record({
  name: undefined as string | undefined | null,
  key: undefined as string | undefined | null,
  symbol: undefined as string | undefined | null,
  shares: undefined as number | undefined | null,
  price: undefined as number | undefined | null,
  priceAsOf: undefined as any,
  value: undefined as number | undefined | null,
  todayPercent: undefined as number | undefined | null,
  todayValue: undefined as number | undefined | null,
  sumOfCostBasis: undefined as number | undefined | null,
  quote: undefined as any,
  quoteDetailed: undefined as DetailedQuote | undefined,
  isCash: undefined as boolean | undefined | null,
  quotesSecurityType: undefined,
  holdings: [] as InvestmentHoldingV2Type[],
  localSecurityId: undefined as string | undefined | null,
  costBasis: undefined as number | undefined | null,
  userCostBasis: undefined as number | undefined | null,
  userCostBasisOn: undefined as string | undefined | null,
  quantityWhenUserSetCostBasis: undefined as number | undefined | null,
  netGain: undefined as number | undefined | null,
  costBasisStatus: undefined as costBasisStatus | undefined | null,
});

type HoldingNodeRecord = ReturnType<typeof mkHoldingNode>;

export const getSymbols = createAppSelector(
  (state: State | undefined, holdings?: ImmutableMap<string, RecordOf<InvestmentHoldingV2Type>>) =>
    holdings || (state && getResourcesById(state)),
  (investmentHoldings) =>
    ImmutableSet<string>().withMutations(
      (symbols) =>
        investmentHoldings &&
        (investmentHoldings as ImmutableMap<string, RecordOf<InvestmentHoldingV2Type>>).reduce(
          (set: ImmutableSet<string>, investmentHolding: RecordOf<InvestmentHoldingV2Type>) =>
            investmentHolding?.symbol ? set.add(investmentHolding.symbol?.trim()) : set,
          symbols,
        ),
    ),
);

// returns a map of the holding id => {value, price, and priceAsOf}
const getHoldingsValues = createSelector(
  getResourcesById,
  selectorsHelpers.createTrimSelector(
    (state: Parameters<typeof investmentQuotesV2Selectors.getQuoteListByHoldingId>[0]) =>
      investmentQuotesV2Selectors.getQuoteListByHoldingId(state),
    (quote: any) => ({
      lastPrice: quote?.lastPrice,
      currentUnitPrice: quote?.currentUnitPrice,
      quoteTime: quote?.quoteTime ? DateTime.fromISO(quote.quoteTime).setZone('America/New_York') : undefined,
      amtChange: quote?.amtChange ?? 0,
    }),
  ),
  selectorsHelpers.createTrimSelector(
    accountsSelectors.getInvestmentAccounts,
    (account: Account) => account?.aggregators?.get(0)?.lastRefreshSuccessfulAt,
  ),
  (state: Parameters<typeof securitiesSelectors.getResourcesById>[0]) => securitiesSelectors.getResourcesById(state),
  (state: Parameters<typeof featureFlagsSelectors.getFeatureFlag>[0]) =>
    featureFlagsSelectors.getFeatureFlag(state, 'investmentsDetailedQuotesRelease'),
  (state: Parameters<typeof getPricesByHoldingId>[0]) => getPricesByHoldingId(state),
  (
    holdings,
    quotes,
    invAccountIdsSuccessfulRefreshAt,
    securities,
    investmentsDetailedQuotesRelease,
    pricesByHoldingId,
  ) =>
    holdings.map((holding: RecordOf<InvestmentHoldingV2Type>, id: string) => {
      const quote = quotes.get(id);

      const { accountId, quantity } = holding;

      const security = securities.get(holding.localSecurityId);
      const holdingPrice = holding.currentPrice;
      const holdingUpdatedAt = DateTime.fromISO(invAccountIdsSuccessfulRefreshAt.get(accountId)).setZone(
        'America/New_York',
      );

      let quotePrice: number | undefined;
      let quoteUpdatedAt: DateTime | undefined;
      if (investmentsDetailedQuotesRelease) {
        const price = pricesByHoldingId.get(id);
        quotePrice = price?.price;
        quoteUpdatedAt = price?.priceAt ? DateTime.fromISO(price.priceAt) : undefined;
      } else {
        quotePrice = quote.lastPrice;
        quoteUpdatedAt = quote?.quoteTime;
      }

      let price = holdingPrice;
      let priceAsOf: DateTime | undefined = holdingUpdatedAt;
      let isMutualFund = false;
      let useQuotePrice = false;
      let marketValue = holding.marketValue;

      if (quotePrice) {
        if (holding.localSecurityId) {
          if (security?.securityType === SecurityTypeEnum.MUTUALFUND) {
            isMutualFund = true;
          }
        }

        if (isMutualFund) {
          const now = DateTime.now().setZone('America/New_York');
          const fourPmNY = DateTime.now()
            .setZone('America/New_York')
            .set({ hour: 16, minute: 0, second: 0, millisecond: 0 });
          if (now < fourPmNY || (quoteUpdatedAt && quoteUpdatedAt.startOf('day') >= holdingUpdatedAt.startOf('day'))) {
            useQuotePrice = true;
          }
        } else {
          useQuotePrice = true;
        }
      }

      if (useQuotePrice) {
        price = quotePrice;
        priceAsOf = quoteUpdatedAt;
        marketValue = price && quantity ? valueFromPrice(price, quantity, security?.securityType) : holding.marketValue;
      }

      return {
        value: marketValue,
        price,
        priceAsOf,
      };
    }),
);

export const getQuotesDetailedByHoldingId = createAppSelector(
  [
    getResourcesById,
    (state) => securitiesSelectors.getResourcesById(state),
    (state) => investmentsQuotesDetailedSelectors.getDetailedQuotesById(state),
  ],
  (holdings, securities, detailedQuotes) =>
    holdings.map((holding, _holdingId) => {
      const security = securities.get(holding?.localSecurityId ?? '');
      const key = keyFromSecurity(security) ?? keyFromHolding(holding, security?.securityType);
      const detailedQuote = detailedQuotes[key ?? ''];
      return detailedQuote;
    }),
);

// Inputs typed via Parameters<> so the inferred state stays narrow (see getSortedAccountsMap above); with
// untyped inputs createAppSelector widens to the full RootState, which packages/web's store cannot satisfy.
export const getPricesByHoldingId = createAppSelector(
  [
    getResourcesById,
    (state: Parameters<typeof securitiesSelectors.getResourcesById>[0]) => securitiesSelectors.getResourcesById(state),
    (state: Parameters<typeof investmentsQuotesDetailedSelectors.getDetailedQuotesById>[0]) =>
      investmentsQuotesDetailedSelectors.getDetailedQuotesById(state),
  ],
  (holdings, securities, detailedQuotes) =>
    holdings.map((holding, _holdingId) => {
      const security = securities.get(holding?.localSecurityId ?? '');
      const key = keyFromSecurity(security) ?? keyFromHolding(holding, security?.securityType);
      const detailedQuote = detailedQuotes[key ?? ''];
      // price priorities
      // 1. fresh detailed quotes lastPrice
      // 2. agg holding currentPrice (based on currentPriceAt)
      // 2. security currentUnitPrice (based on modifiedAt)
      // 3. agg holding marketValue
      let price;
      let priceAt;
      let source: 'QUOTE' | 'HOLDING_PRICE' | 'SECURITY' | 'HOLDING_VALUE' | undefined;
      if (typeof detailedQuote?.lastPrice === 'number') {
        price = detailedQuote?.lastPrice;
        priceAt = detailedQuote?.quoteTime;
        source = 'QUOTE';
      } else if (
        typeof holding.currentPrice === 'number' &&
        (holding.currentPriceAt ?? '') > (security?.modifiedAt ?? '')
      ) {
        price = holding.currentPrice;
        priceAt = holding.currentPriceAt;
        source = 'HOLDING_PRICE';
      } else if (
        typeof security?.currentUnitPrice === 'number' &&
        (holding.currentPriceAt ?? '') < (security?.modifiedAt ?? '')
      ) {
        price = security?.currentUnitPrice;
        priceAt = security?.modifiedAt;
        source = 'SECURITY';
      } else if (typeof holding.quantity === 'number' && typeof holding.marketValue === 'number' && holding.quantity) {
        price = (holding.marketValue / holding.quantity) * priceScaleForSecurityType(security?.securityType);
        source = 'HOLDING_VALUE';
      }
      return { price, priceAt, source };
    }),
);

export const getPriceByHoldingId = (
  state: Parameters<typeof getPricesByHoldingId>[0],
  holdingId: string,
): number | undefined => getPricesByHoldingId(state).get(holdingId)?.price;

export const getValuesByHoldingId = createAppSelector(
  [getResourcesById, getPricesByHoldingId, getSecurityTypesById],
  (holdings, pricesByHoldingId, securityTypesById) =>
    holdings.map((holding, holdingId) => {
      const price = pricesByHoldingId.get(holdingId)?.price;
      const value =
        typeof holding.quantity === 'number' && typeof price === 'number'
          ? valueFromPrice(price, holding.quantity, securityTypesById.get(holding.localSecurityId ?? ''))
          : holding.marketValue;
      return value;
    }),
);

const getHoldingChanges = createSelector(
  selectorsHelpers.createTrimSelector(getResourcesById, (holding) => ({
    quantity: holding.quantity ?? 0,
    localSecurityId: holding.localSecurityId,
  })),
  selectorsHelpers.createTrimSelector(
    (state: Parameters<typeof investmentQuotesV2Selectors.getQuoteListByHoldingId>[0]) =>
      investmentQuotesV2Selectors.getQuoteListByHoldingId(state),
    (quote) => quote,
  ),
  getQuotesDetailedByHoldingId,
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'investmentsDetailedQuotesRelease'),
  getSecurityTypesById,
  (trimmedHoldings, amtChanges, quotesDetailedByHoldingId, investmentsDetailedQuotesRelease, securityTypesById) =>
    trimmedHoldings.map(({ quantity, localSecurityId }: { quantity: number; localSecurityId?: string }, id: string) => {
      const securityType = securityTypesById.get(localSecurityId ?? '');
      if (investmentsDetailedQuotesRelease) {
        const amtChange = quotesDetailedByHoldingId.get(id)?.amtChange;
        return typeof amtChange === 'number' ? valueFromPrice(amtChange, quantity, securityType) : undefined;
      } else {
        return valueFromPrice(amtChanges.get(id)?.amtChange ?? 0, quantity, securityType);
      }
    }),
);

export const getHoldingProfit = createSelector(
  selectorsHelpers.createTrimSelector(getResourcesById, (holding) => ({
    value: holding.marketValue,
    costBasis: holding.costBasis ?? holding.userCostBasis,
    quantity: holding.quantity,
    isCash: holding.isCash,
    isUserCostBasisSet: holding.userCostBasis !== undefined,
    quantityWhenUserSetCostBasis: holding.quantityWhenUserSetCostBasis,
  })),
  (state) => investmentQuotesV2Selectors.getQuoteListByHoldingId(state),
  (state) => getValuesByHoldingId(state),
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'investmentsDetailedQuotesRelease'),
  (trimmedHoldings, lastQuotes, valuesByHoldingId, investmentsDetailedQuotesRelease) =>
    trimmedHoldings.map((trimmedHolding: any, id: string) => {
      const lastQuote = lastQuotes.get(id);
      const quantityChangeNeeded =
        trimmedHolding.isUserCostBasisSet &&
        trimmedHolding.quantityWhenUserSetCostBasis &&
        trimmedHolding.quantity !== trimmedHolding.quantityWhenUserSetCostBasis;
      let lastValue;
      if (investmentsDetailedQuotesRelease) {
        const value = valuesByHoldingId.get(id);
        lastValue = value;
      } else {
        lastValue = lastQuote?.lastPrice * trimmedHolding.quantity || trimmedHolding.value || 0;
      }
      if (!lastValue || trimmedHolding.costBasis == null || quantityChangeNeeded) {
        return {
          profit: 0,
          costBasis: undefined,
          isCash: trimmedHolding.isCash,
          quantity: trimmedHolding.quantity,
        };
      }
      return {
        profit: lastValue - trimmedHolding.costBasis,
        costBasis: trimmedHolding.costBasis,
        quantity: trimmedHolding.quantity,
      };
    }),
);

type HoldingsTotals = {
  todaysBalance: number | undefined;
  todaysBalanceChange: number | undefined;
  totalProfit: number | undefined;
  totalCostBasis: number | false | undefined;
  totalCash: number | undefined;
};

export const getHoldingsTotals = createCachedSelector(
  getHoldingsValues,
  getHoldingChanges,
  (_state: State, accounts: any) => accounts,
  getInvestmentHoldingsIdsByAccountId,
  getHoldingProfit,
  (holdingValues, holdingChanges, accountIds, holdingIdsByAccountId, totalCostBasis) =>
    accountIds
      ?.map((id: any) => (holdingIdsByAccountId as any).get(id))
      .flatten()
      .reduce(
        (accumulator: HoldingsTotals, holdingId: string) => {
          const todaysBalance = holdingValues.get(holdingId)?.value;
          const todaysBalanceChange = holdingChanges.get(holdingId);
          const totalCostBasisInfo = totalCostBasis.get(holdingId);
          const profit = totalCostBasisInfo?.profit;
          const costBasis = totalCostBasisInfo?.costBasis;
          const isCash = totalCostBasisInfo?.isCash;
          const isZeroQuantity = totalCostBasisInfo?.quantity === 0;
          accumulator.totalCash =
            isCash && typeof todaysBalance === 'number'
              ? typeof accumulator.totalCash === 'number'
                ? accumulator.totalCash + todaysBalance
                : todaysBalance
              : accumulator.totalCash;
          accumulator.todaysBalance =
            typeof accumulator.todaysBalance === 'number'
              ? accumulator.todaysBalance + (todaysBalance || 0)
              : todaysBalance;

          accumulator.todaysBalanceChange =
            typeof accumulator.todaysBalanceChange === 'number'
              ? accumulator.todaysBalanceChange + (todaysBalanceChange || 0)
              : todaysBalanceChange;
          accumulator.totalProfit = isCash
            ? accumulator.totalProfit
            : typeof accumulator.totalProfit === 'number'
              ? accumulator.totalProfit + (profit || 0)
              : profit;
          accumulator.totalCostBasis =
            isCash || !totalCostBasisInfo || isZeroQuantity
              ? accumulator.totalCostBasis
              : accumulator.totalCostBasis === undefined || costBasis === undefined
                ? undefined
                : typeof accumulator.totalCostBasis === 'number'
                  ? accumulator.totalCostBasis + (costBasis || 0)
                  : costBasis;
          return accumulator;
        },
        {
          todaysBalance: undefined,
          todaysBalanceChange: undefined,
          totalProfit: undefined,
          totalCostBasis: false,
          totalCash: undefined,
        } as HoldingsTotals,
      ),
)({
  keySelector: (_state: unknown, accounts: any) => accounts?.hashCode() ?? 'no-accounts',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

/** Account-tree totals only need investment accounts; balances and labels do not change membership. */
export const getInvestmentHoldingsTotalsByAccountId = createAppSelector(
  getHoldingsValues,
  accountsSelectors.getInvestmentAccountIds,
  getInvestmentHoldingsByAccountId,
  (holdingValues, accountIds, holdingsByAccountId): ImmutableMap<string, number> =>
    ImmutableMap<string, number>().withMutations((totals) => {
      accountIds.forEach((accountId) => {
        let total = 0;
        holdingsByAccountId.get(accountId)?.forEach((holding) => {
          total += holdingValues.get(holding.id)?.value || 0;
        });
        totals.set(accountId, total);
      });
    }),
);

export const getHoldingNodesByHoldingId = createAppSelector(
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'investmentsDetailedQuotesRelease'),
  (state) => investmentQuotesV2Selectors.getQuoteListByHoldingId(state),
  getHoldingsValues,
  getHoldingChanges,
  getResourcesById,
  selectorsHelpers.createTrimSelector(
    accountsSelectors.getInvestmentAccounts,
    (account: Account) => account?.aggregators?.get(0)?.lastRefreshSuccessfulAt,
  ),
  (state) => securitiesSelectors.getResourcesById(state),
  (state) => investmentsQuotesDetailedSelectors.getDetailedQuotesById(state),
  (
    investmentsDetailedQuotesRelease: boolean | undefined,
    quotes: any,
    values: any,
    changes: any,
    holdings: ImmutableMap<string, RecordOf<InvestmentHoldingV2Type>>,
    invAccountIdsSuccessfulRefreshAt: any,
    securities: any,
    quotesDetailed: any,
  ) => {
    const visitedAccounts = new Map();
    return holdings.map((holding: RecordOf<InvestmentHoldingV2Type>, id: string) => {
      let lastUpdatedAggPrice = visitedAccounts.get(holding.accountId) || null;
      if (!lastUpdatedAggPrice && !visitedAccounts.has(holding.accountId)) {
        lastUpdatedAggPrice = invAccountIdsSuccessfulRefreshAt.get(holding.accountId);
        visitedAccounts.set(holding.accountId, lastUpdatedAggPrice);
      }
      const quote = quotes.get(id); // Can be the security OR connected quote
      let value = values.get(id).value;
      let todayValue = changes.get(id);
      let todayPercent = quote?.pctChange;

      let name: string | undefined | null;
      let key: string | undefined | null;
      let symbol: string | undefined | null;
      let price: number | undefined = values.get(id).price;
      const priceAsOf = values.get(id).priceAsOf;
      let quotesSecurityType;

      const costBasis = holding?.costBasis;
      const userCostBasis = holding?.userCostBasis;
      const userCostBasisOn = holding?.userCostBasisOn;
      const quantityWhenUserSetCostBasis = holding?.quantityWhenUserSetCostBasis;
      const noCostBasis =
        holding.quantity && !holding?.isCash && holding?.costBasis == null && holding?.userCostBasis == null;
      const quantityChangeNeeded =
        holding?.userCostBasis != null &&
        quantityWhenUserSetCostBasis &&
        holding.quantity !== quantityWhenUserSetCostBasis;
      const holdingCostBasisStatus: costBasisStatus = {
        noCostBasis: !!noCostBasis,
        ...(holding.quantity &&
          holding?.costBasis == null &&
          (noCostBasis || quantityChangeNeeded) && {
            incompleteCostBasisHoldings: [holding as unknown as InvestmentHoldingV2Type],
          }),
      };
      let quoteDetailed;
      if (investmentsDetailedQuotesRelease) {
        const security = securities.get(holding?.localSecurityId);
        key = keyFromSecurity(security) || security?.id || keyFromHolding(holding, security?.securityType);
        symbol = holding.isCash ? null : security?.symbol || holding?.symbol;
        name = holding.isCash ? null : security?.name || holding.securityName || '';
        quoteDetailed = quotesDetailed[key ?? ''];
        price = quoteDetailed?.lastPrice || security?.currentUnitPrice || holding?.currentPrice || null;
        value =
          price && holding?.quantity
            ? valueFromPrice(price, holding.quantity, security?.securityType)
            : holding?.marketValue || null;
        // amtChange is per share; changes already holds the holding's dollar change.
        todayValue = changes.get(id) || null;
        todayPercent = quoteDetailed?.pctChange || null;
        quotesSecurityType = quoteDetailed?.securityType;
      } else {
        name = holding.isCash
          ? undefined
          : (quote && quote.name) || holding.securityName || holding.symbol || undefined;
        key = holding.isCash ? 'Simplifi-Cash' : holding.symbol?.trim() || name;

        symbol = holding?.symbol?.trim();
      }
      const finalCostBasis = costBasis ?? userCostBasis;
      const netGain = finalCostBasis != null ? Math.round((value - finalCostBasis) * 10000) / 10000 : undefined;
      return mkHoldingNode({
        name,
        key,
        symbol,
        shares: holding.isCash ? null : holding.quantity,
        price,
        priceAsOf,
        value,
        todayPercent,
        todayValue,
        sumOfCostBasis: finalCostBasis,
        quote,
        quoteDetailed,
        isCash: holding.isCash,
        quotesSecurityType,
        holdings: [holding as unknown as InvestmentHoldingV2Type],
        localSecurityId: holding?.localSecurityId,
        costBasis,
        userCostBasis,
        userCostBasisOn,
        quantityWhenUserSetCostBasis,
        netGain,
        costBasisStatus: holdingCostBasisStatus,
      });
    });
  },
);

const sumHoldingNodes = (oldOrEmpty: HoldingNodeRecord | null, newNode: HoldingNodeRecord) =>
  oldOrEmpty
    ? newNode
        .updateIn(['value'], (newValue: any) => (newValue ?? 0) + (oldOrEmpty.value ?? 0))
        .updateIn(['price'], (newPrice: any) => {
          return newNode.priceAsOf > oldOrEmpty.priceAsOf ? newPrice : oldOrEmpty.price;
        })
        .updateIn(['todayValue'], (newTodayValue: any) => (newTodayValue ?? 0) + (oldOrEmpty.todayValue ?? 0))
        .updateIn(['shares'], (newShares: any) => (newShares ?? 0) + (oldOrEmpty.shares ?? 0))
        .updateIn(['holdings'], (newHoldings: any) => [...(newHoldings ?? []), ...(oldOrEmpty.holdings ?? [])])
        .updateIn(['costBasis'], (newCostBasis: any) => {
          const oldCostBasis = oldOrEmpty.costBasis;
          if (newCostBasis == null) return oldCostBasis;
          if (oldCostBasis == null) return newCostBasis;
          return newCostBasis + oldCostBasis;
        })
        .updateIn(['sumOfCostBasis'], () => {
          const costBasis = newNode.sumOfCostBasis;
          const oldCostBasis = oldOrEmpty.sumOfCostBasis;
          if (costBasis == null) return oldCostBasis;
          if (oldCostBasis == null) return costBasis;
          return costBasis + oldCostBasis;
        })
        .updateIn(['userCostBasis'], (newUserCostBasis: any) => {
          const oldUserCostBasis = oldOrEmpty.userCostBasis;
          if (newUserCostBasis == null) return oldUserCostBasis;
          if (oldUserCostBasis == null) return newUserCostBasis;
          return newUserCostBasis + oldUserCostBasis;
        })
        .updateIn(['netGain'], (newNetGain: any) => {
          const oldNetGain = oldOrEmpty.netGain;
          if (newNetGain == null) return oldNetGain;
          if (oldNetGain == null) return newNetGain;
          return newNetGain + oldNetGain;
        })
        .updateIn(['costBasisStatus'], (newCostBasisStatus: any) => ({
          noCostBasis: !!newCostBasisStatus?.noCostBasis && !!oldOrEmpty.costBasisStatus?.noCostBasis,
          incompleteCostBasisHoldings: newCostBasisStatus?.incompleteCostBasisHoldings
            ? [
                ...newCostBasisStatus.incompleteCostBasisHoldings,
                ...(oldOrEmpty.costBasisStatus?.incompleteCostBasisHoldings ?? []),
              ]
            : oldOrEmpty.costBasisStatus?.incompleteCostBasisHoldings,
        }))
    : newNode;

export const getInvestmentHoldingNodes = createCachedSelector(
  getHoldingNodesByHoldingId,
  (state: any, accounts: any) =>
    List.isList(accounts)
      ? accounts
      : accountsSelectors.accountIds(state, accounts || accountsSelectors.getInvestmentAccounts(state)),
  getInvestmentHoldingsIdsByAccountId,
  (holdingNodes, accountIds, holdingsByAccountId) => {
    const relevantHoldingNodes = (accountIds as any)
      .map((id: any) => (holdingsByAccountId as any).get(id))
      .toList()
      .flatten()
      .map((holdingId: any) => holdingNodes.get(holdingId));

    return ImmutableMap()
      .withMutations((draft) =>
        relevantHoldingNodes.reduce((acc: any, node: any) => {
          if (!node) {
            return acc;
          }
          return acc.set(node.key, sumHoldingNodes(acc.get(node.key), node));
        }, draft),
      )
      .toList();
  },
)({
  keySelector: (_state: unknown, accounts: any) => accounts?.hashCode() ?? 'no-accounts',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
}) as unknown as (state: any, accounts?: any) => List<HoldingNodeType>;

export const getStockString = createAppSelector(getInvestmentHoldingNodes, (nodes) =>
  nodes
    ?.filter((node: HoldingNodeType) => node?.symbol && node?.quote)
    .map((node: HoldingNodeType) => node?.symbol?.trim())
    .join(','),
);

export const getAllStockSymbols = createAppSelector(getInvestmentHoldingNodes, (nodes) =>
  nodes
    ?.filter((node: HoldingNodeType) => node?.symbol && node?.quote)
    .map((node: HoldingNodeType) => node?.symbol?.trim()),
);

export const getSortedHoldingNodes = createCachedSelector(
  (state: State, props: any) => getInvestmentHoldingNodes(state, props.accounts),
  (_state: State, props: any) => props.sortDirection,
  (_state: State, props: any) => props.orderBy,
  (nodes, sortDirection, orderBy) =>
    nodes?.sortBy(
      (myStock: HoldingNodeType) => (myStock as any)[orderBy],
      (unalteredValueA: unknown, unalteredValueB: unknown) => {
        if (orderBy === columns[0].key || orderBy === columns[1].key) {
          const last = String.fromCharCode(0xffff);
          const valueA = unalteredValueA ? String(unalteredValueA) : last;
          const valueB = unalteredValueB ? String(unalteredValueB) : last;
          if (sortDirection === 'asc') {
            return valueA.localeCompare(valueB, undefined);
          }
          return valueB.localeCompare(valueA, undefined);
        }

        const last = Number.NEGATIVE_INFINITY;
        const valueA = unalteredValueA != null ? Number(unalteredValueA) : last;
        const valueB = unalteredValueB != null ? Number(unalteredValueB) : last;

        return (valueA - valueB) * (sortDirection === 'asc' ? -1 : 1);
      },
    ),
)({
  keySelector: (_state: unknown, props: any) =>
    `${props?.context ?? ''}_${
      isImmutable(props?.accounts) ? props?.accounts?.hashCode() : props?.accounts?.toString()
    }_${props?.sortDirection}_${props?.orderBy}`,
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const getSortedHoldingNodesForDashboard = createAppSelector(
  (state: State) => (getInvestmentHoldingNodes as any)(state),
  (_state: unknown, props: any) => props.orderBy,
  (_state: unknown, props: any) => props.amountOfNodes,
  (nodes, orderBy, amountOfNodes = 10) =>
    nodes
      .filter((item: HoldingNodeType) => !!(item as any)[orderBy])
      ?.sortBy((myStock: HoldingNodeType) => Math.abs((myStock as any)[orderBy]) * -1)
      .slice(0, amountOfNodes)
      ?.sortBy((myStock: HoldingNodeType) => (myStock as any)[orderBy] * -1),
) as unknown as (state: any, props: any) => List<HoldingNodeType>;

const getHoldingQuoteTime = (node: HoldingNodeType | null): string | undefined => {
  const quoteTime = node?.quoteDetailed?.quoteTime ?? node?.quote?.quoteTime;
  return typeof quoteTime === 'string' ? quoteTime : undefined;
};

export const getMostRecentStock = createAppSelector(
  (state: State, accounts: any) => getInvestmentHoldingNodes(state, accounts),
  (nodes): HoldingNodeType | null => {
    let recentNode: HoldingNodeType | null = null;
    let recentQuoteTimestamp = Number.NEGATIVE_INFINITY;
    nodes.forEach((node: HoldingNodeType) => {
      const quoteTime = getHoldingQuoteTime(node);
      const quoteTimestamp = quoteTime ? DateTime.fromISO(quoteTime).toMillis() : Number.NaN;
      if (!Number.isNaN(quoteTimestamp) && quoteTimestamp > recentQuoteTimestamp) {
        recentNode = node;
        recentQuoteTimestamp = quoteTimestamp;
      }
    });
    return recentNode;
  },
);

export const getTimeStrings = createCachedSelector(
  getMostRecentStock,
  getHoldingsTotals,
  (mostRecentStock, holdingTotals) => {
    const originalTime = getHoldingQuoteTime(mostRecentStock);
    if (!originalTime) {
      return '\xA0';
    }
    const originalDateTime = DateTime.fromISO(originalTime);
    const timeString = originalDateTime.toFormat('MMM dd, yyyy • h:mm a ZZZZ');

    const { todaysBalance, todaysBalanceChange } = holdingTotals;

    const todaysPercentageChange =
      typeof todaysBalance === 'number' &&
      typeof todaysBalanceChange === 'number' &&
      todaysBalance !== todaysBalanceChange
        ? (100 * todaysBalanceChange) / (todaysBalance - todaysBalanceChange)
        : undefined;
    const todaysPercentageChangeString = todaysPercentageChange
      ? `(${todaysPercentageChange > 0 ? '+' : ''}${todaysPercentageChange.toFixed(2)}%)`
      : '(-)';

    return { timeString, todaysPercentageChange, todaysPercentageChangeString };
  },
)({
  keySelector: (_state: unknown, accounts: any) => accounts?.hashCode() ?? 'no-accounts',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
}) as unknown as (
  state: any,
  accounts?: any,
) => { timeString: string; todaysPercentageChange: number | undefined; todaysPercentageChangeString: string };

export const getHoldingByAccountIdAndSymbolOrName = createAppSelector(
  (state: State, props: { accountId?: string; symbol?: string; holdingName?: string }) => {
    if (!props.accountId) {
      return undefined;
    }
    return getInvestmentHoldingsForAccount(state, props.accountId);
  },
  (_state: State, props: { accountId?: string; symbol?: string; holdingName?: string }) => props.accountId,
  (_state: State, props: { accountId?: string; symbol?: string; holdingName?: string }) => props.symbol || '',
  (_state: State, props: { accountId?: string; symbol?: string; holdingName?: string }) => props.holdingName || '',
  (holdings, accountId, symbol, holdingName) =>
    (accountId &&
      holdingName &&
      holdings &&
      holdings.find((holding) =>
        Boolean(
          (holding?.accountId === accountId && symbol && holding?.symbol?.trim() === symbol) ||
            (holdingName === 'Simplifi-Cash' && holding.isCash) ||
            (holdingName && holding?.securityName === holdingName && symbol && holding?.symbol?.trim() === symbol) ||
            (holdingName &&
              !symbol &&
              ((holding as any)?.name === holdingName || holding?.securityName === holdingName)),
        ),
      )) ||
    undefined,
);

export const getHoldingNodeByKey = createAppSelector(
  (state: State, props: { accounts?: any; holdingNodeKey?: string }) =>
    getInvestmentHoldingNodes(state, props.accounts),
  (_state: State, props: { accounts?: any; holdingNodeKey?: string }) => props.holdingNodeKey,
  (nodes, holdingNodeKey) => nodes?.find((node: HoldingNodeType) => node?.key === holdingNodeKey),
);

// A store reset drops every key's selector (see selectorCacheRegistry).
registerCachedSelectors(getHoldingsTotals, getInvestmentHoldingNodes, getSortedHoldingNodes, getTimeStrings);
